// @flow
import React, {useEffect, useState, useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {useHistory, useLocation} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {
  Grid,
  Typography,
  Hidden,
  MenuItem,
  useMediaQuery,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import EditCategoryModal from '../../categories/components/EditCategoryModal/EditCategoryModal';
import Skeleton from '@material-ui/lab/Skeleton';
import AlertDueDateTier from '../../tiers/components/AlertDueDateTier/AlertDueDateTier';
import ProductCard from '../../../elements/ProductCard/ProductCard';
import FeaturedProductCard from '../../../elements/ProductCard/FeaturedProductCard';
import SnackBar from '../../../elements/SnackBar/SnackBar';
import HorizontalFilter from '../../../elements/HorizontalFilter/HorizontalFilter';
import FilterButton from '../../../elements/HorizontalFilter/FilterButton/FilterButton';
import HorizontalScroll from '../../../elements/HorizontalFilter/HorizontalScroll/HorizontalScroll';
import StockChangeModal from './StockChangeModal/StockChangeModal';
import {colors} from '../../../assets/styles/colors';
import {useIsFeatureInTier} from '../../../utils/component';
import TierRestrictionModal from '../../../elements/TierRestrictionModal/TierRestrictionModal';
import {usePage} from '../../page/utils';

import SearchBar from '../../../elements/SearchBar/SearchBar';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import IconButton from '@material-ui/core/IconButton';
import {
  AddCircleOutline,
  Edit,
  PlayCircleFilledTwoTone,
  ExpandLess,
  ExpandMore,
} from '@material-ui/icons';
import SortableList from 'react-easy-sort';
import {classNames} from '../../../utils/react/className';
import CustomSelect from '../../../elements/CustomSelect/CustomSelect';

import type {
  Category,
  User,
  Currency,
  StoreInformation,
  Product,
  Store,
  ResellerInformation,
} from '../../../entities';
import type {CategoryRequest} from '../../categories/api';
import type {ProductRequest, EditProductVariantRequest} from '../api';
import type {ApiError} from '../../../http/error';
import {scrollTop} from '../../../utils/dom';
import {usePrevious} from '../../../utils/react';
import InstagramProductsModal from './InstagramProductsModal/InstagramProductsModal';
import miniInstagramIcon from '../../../assets/img/miniInstagramIcon.png';
import {getImageUrl} from '../../../utils/image_provider';
import {TIENDADA_LINKS} from '../../../constants';
import {useSearchProductEnabled} from '../hooks/useSearchProductEnabled';
import TiendadaTooltip from '../../../elements/tiendada-tooltip';
import {useStoreBusinessModel} from '../../store/utils';
import {arrayMove} from '../../../utils/array';

type Props = {|
  user: User,
  fetchProducts: ({queryFilter?: {[key: string]: string}}) => void,
  fetchFeaturedProducts: () => void,
  createProduct: ({productRequest: ProductRequest}) => void,
  createProductSuccess?: boolean,
  editProduct: ({productRequest: ProductRequest}) => void,
  editProductSuccess?: boolean,
  enableProduct: ({productRequest: ProductRequest}) => void,
  enableProductSuccess?: boolean,
  disableProductSuccess?: boolean,
  productFeaturedSuccess?: boolean,
  productUnfeaturedSuccess?: boolean,
  stockChangeSuccess?: boolean,
  enableProductError?: string,
  editProductVariant: ({
    editProductVariantRequest: EditProductVariantRequest,
  }) => void,
  deleteProduct: ({productRequest: ProductRequest}) => void,
  deleteProductSuccess?: boolean,
  products: Product[],
  featuredProducts: Product[],
  isFetchingFeatured?: boolean,
  pagination?: Pagination,
  queryFilter?: Object,
  isFetching: boolean,
  isSaving: boolean,
  productOperationError?: ApiError | string,
  store?: Store,
  currency?: Currency,
  // information?: Information,
  storeInformation?: StoreInformation,
  resellerInformation?: ResellerInformation,
  categories?: Category[],
  isSavingCategory?: boolean,
  createCategory: ({categoryRequest: CategoryRequest}) => void,
  createCategoryError?: ApiError,
  createCategorySuccess?: boolean,
  editCategory: ({categoryRequest: CategoryRequest}) => void,
  sortCategories: ({sortMovement?: {[key: string]: any}}) => void,
  sortCategoriesSuccess?: boolean,
  resetCategoryState: () => void,
  resetProductState: () => void,
  fetchProductsCatalogue: () => void,
  resetState: () => void,
  createProductsCatalogue: () => void,
  catalogue?: {[key: string]: any},
  isCreatingCatalogue?: boolean,
  createCatalogueError?: boolean,
  createCatalogueSuccess?: boolean,
  scheduleShipment: boolean,
  deleteCategory: ({categoryRequest: CategoryRequest}) => void,
  deleteCategorySuccess?: boolean,
  editCategoryError?: boolean,
  editCategorySuccess?: boolean,
|};

const drawerWidth = 264;

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  // title: {
  //   marginTop: theme.spacing(3),
  // },
  navBackground: {
    [theme.breakpoints.down('sm')]: {
      background: 'white !important',
    },
  },
  dividerVertical: {
    margin: theme.spacing(0, 1),
    background: colors.grey300,
  },
  toolbar: theme.mixins.toolbar,
  drawer: {
    flexShrink: 0,
    // position: 'relative',
    // whiteSpace: 'nowrap',
    width: drawerWidth,
  },
  headerWrapper: {
    background: '#f8f9fb',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
    [theme.breakpoints.down('xs')]: {
      fontSize: '18px',
    },
  },
  titleCategories: {
    fontSize: '1rem',
    fontWeight: 600,
  },
  instagramProductUploadButton: {
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    margin: '0 14px 0 0',
    [theme.breakpoints.down('xs')]: {
      backgroundColor: 'unset',
      '&:hover': {
        background: 'unset',
      },
      color: colors.purple3,
      fontSize: '12px',
      margin: '0 16px 0 0',
      padding: 0,
      boxShadow: 'unset',
    },
  },
  improveTierButton: {
    backgroundColor: 'white',
    color: colors.purple3,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    margin: '0 14px 0 0',
    [theme.breakpoints.down('xs')]: {
      backgroundColor: 'unset',
      color: colors.purple3,
      fontSize: '12px',
      margin: '0 16px 0 0',
      padding: 0,
      boxShadow: 'unset',
    },
  },
  tutorialsButton: {
    backgroundColor: 'white',
    color: colors.purple3,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    [theme.breakpoints.down('xs')]: {
      backgroundColor: 'unset',
      color: colors.purple3,
      padding: 0,
      boxShadow: 'unset',
    },
  },
  categoriesContainer: {
    marginRight: theme.spacing(-4),
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(0, -2),
    },
  },
  editCategoriesButton: {
    height: 32,
    fontSize: '0.875rem',
    fontWeight: 500,
    marginRight: 12,
    // padding: '0 0 0 8px',
  },
  mobileEditCategoriesButton: {
    backgroundColor: 'unset',
    color: colors.purple3,
    padding: '0 0 0 8px',
  },
  content: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    // overflowY: 'auto',
    // height: '100%',
    background: '#f8f9fb',
    // overflow: 'auto',
    padding: '.5rem',
    [theme.breakpoints.up('md')]: {
      padding: '1rem',
    },
  },
  productsContent: {
    // flexGrow: 1,
    // height: '91vh',
    // overflow: 'auto',
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    padding: '1rem .5rem',
    background: 'white',
    [theme.breakpoints.up('md')]: {
      padding: '1rem',
    },
  },
  productosDestacados: {
    display: 'flex',
    flexDirection: 'column',
  },
  productosDestacadosTitle: {
    fontSize: '1rem',
    marginTop: '1rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    userSelect: 'none',
  },
  productosDestacadosTitleText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  productosDestacadosCollapseButton: {
    padding: theme.spacing(0.5),
    color: colors.grey600,
    '&:hover': {
      color: colors.purple3,
    },
  },
  productosDestacadosSubtitle: {
    fontSize: '0.875rem',
    fontColor: colors.grey700,
    marginTop: theme.spacing(1),
  },
  productosDestacadosSlider: {
    display: 'flex',
    marginTop: '1rem',
    height: 316,
    marginBottom: -16,
    width: '100%',
    overflowX: 'auto',
    [theme.breakpoints.down('xs')]: {
      height: 256,
    },
  },
  productosDestacadosSliderCollapsed: {
    display: 'none',
  },
  sortableContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(2),
    },
  },
  draggedItem: {
    opacity: 0.8,
    transform: 'scale(1.02)',
    boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
    zIndex: 1000,
  },
  productsWrap: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
    // [theme.breakpoints.up('md')]: {
    //   flexDirection: 'row',
    //   marginTop: theme.spacing(3),
    // },
  },
  inventoryTitle: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
  },
  bookServices: {
    fontSize: 16,
    background: colors.purple3,
    color: 'white',
    textDecoration: 'none',
    padding: '5px 20px',
    fontWeight: 600,
    borderRadius: 30,
    '&:hover': {
      textDecoration: 'none',
    },
  },
  tutorialsText: {
    fontSize: 14,
    paddingLeft: 5,
  },
  topBar: {
    margin: '2.25rem 0 .75rem 0',
  },
  totalItems: {
    fontSize: '0.875rem',
    fontColor: colors.grey700,
  },
  filterArea: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterLabel: {
    fontSize: '0.875rem',
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  paginationArea: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '.75rem',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  pagination: {
    '& .Mui-selected': {
      backgroundColor: colors.lightPurple,
      color: colors.purple3,
    },
    '& .Mui-selected:hover': {
      backgroundColor: colors.lightPurple,
      // color: colors.purple3,
    },
  },
  sizeSelect: {
    [theme.breakpoints.down('xs')]: {
      marginTop: 0,
    },
  },
  addOptionText: {
    [theme.breakpoints.down('sm')]: {
      fontWeight: 400,
      marginRight: theme.spacing(3),
    },
    fontWeight: 600,
    padding: theme.spacing(1.5, 0),
    color: theme.palette.primary.main,
    cursor: 'pointer',
  },
  addOptionButton: {
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(2),
    },
    marginBottom: theme.spacing(2),
    textTransform: 'none',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      opacity: 0.8,
    },
  },
  mobileShow: {
    marginBottom: 16,
    textAlign: 'center',
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  desktopShow: {
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'inline-block',
    },
  },
  wrapActionProducts: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
    },
  },
  downloadCatalogue: {
    padding: 'auto 1rem',
    width: '215px',
    [theme.breakpoints.up('md')]: {
      marginRight: '0.5rem',
    },
  },
  spinner: {
    height: '24px!important',
    width: '24px!important',
  },
  subRouteNameWrap: {
    height: 56,
    position: 'sticky',
    top: 72,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    color: colors.grey700,
    backgroundColor: colors.grey200,
    padding: theme.spacing(2),
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  categoryDisabled: {
    opacity: 0.5,
  },
}));

export default usePage<Props>(Products, 'Products');

const LIMIT_TRIES = 10;
const INTERVAL = 18000;
function Products(props: Props) {
  const {
    fetchProducts,
    fetchFeaturedProducts,
    // createProduct,
    createProductSuccess,
    // editProduct,
    editProductSuccess,
    enableProduct,
    enableProductSuccess,
    productFeaturedSuccess,
    productUnfeaturedSuccess,
    stockChangeSuccess,
    disableProductSuccess,
    enableProductError,
    editProductVariant,
    // deleteProduct,
    deleteProductSuccess,
    products,
    featuredProducts,
    isFetchingFeatured,
    pagination,
    queryFilter,
    isFetching,
    isSaving,
    // productOperationError,
    store,
    currency,
    catalogue,
    isCreatingCatalogue,
    createCatalogueError,
    createCatalogueSuccess,
    categories,
    createCategory,
    createCategoryError,
    // resetCategoryState,
    resetProductState,
    isSavingCategory,
    editCategory,
    createCategorySuccess,
    sortCategories,
    fetchProductsCatalogue,
    deleteCategory,
    sortCategoriesSuccess,
    deleteCategorySuccess,
    editCategoryError,
    editCategorySuccess,
    resetState,
  } = props;

  const classes = useStyles();

  const location = useLocation();
  const history = useHistory();

  const mobile = useMediaQuery('(max-width:600px)');

  const businessModel = useStoreBusinessModel();

  const [isFetchingCatalogue, setIsFetchingCatalogue] = useState();
  const [showMessage, setShowMessage] = useState(false);
  const [count, setCount] = useState(0);
  const previousCount = usePrevious(count);

  const [categoriesSelected, setCategoriesSelected] = useState(
    queryFilter && queryFilter.categories
      ? queryFilter.categories.split(',')
      : []
  );

  const getPageFromQuery = (search: string) => {
    const q = queryString.parse(search);
    const p = parseInt(q.page, 10);
    return p;
  };
  const p = getPageFromQuery(location.search);
  const getSizeFromQuery = (search: string) => {
    const q = queryString.parse(search);
    const p = parseInt(q.size, 10);
    return p;
  };
  const s = getSizeFromQuery(location.search);
  const [page, setPage] = useState(
    isNaN(p) ? (pagination && pagination.page ? pagination.page + 1 : 1) : p
  );
  const [size, setSize] = useState(
    isNaN(s) ? (pagination && pagination.size ? pagination.size : 16) : s
  );
  const [filter, setFilter] = useState('');
  const [filterObject, setFilterObject] = useState<{
    enabled?: number,
    order?: string,
    sort?: string,
    has_stock?: number,
  }>(queryFilter ? queryFilter : {});

  const handleSortChange = event => {
    if (event) {
      setFilter(event.target.value);
      switch (event.target.value) {
        case '--':
          setFilterObject({});
          break;
        // case 'Productos con stock':
        //   setFilterObject({stock: 999999});
        //   break;
        case 'Productos sin stock':
          setFilterObject({has_stock: 0});
          break;
        case 'Productos habilitados':
          setFilterObject({enabled: 1});
          break;
        case 'Productos deshabilitados':
          setFilterObject({enabled: 0});
          break;
        case 'Nombre A-Z':
          setFilterObject({order: 'ASC', sort: 'NAME'});
          break;
        case 'Nombre Z-A':
          setFilterObject({order: 'DESC', sort: 'NAME'});
          break;
        case 'Precio Ascendente':
          setFilterObject({order: 'ASC', sort: 'PRICE'});
          break;
        case 'Precio Descendente':
          setFilterObject({order: 'DESC', sort: 'PRICE'});
          break;
        default:
          break;
      }
    }
  };

  const getSearchFromQuery = (search: string) => {
    const s = queryString.parse(search);
    return s;
  };

  const buildQueryParamsCallback = useCallback((): {[key: string]: any} => {
    const params: {[key: string]: any} = {
      order: filterObject.order,
      sort: filterObject.sort,
      enabled: filterObject.enabled,
      has_stock: filterObject.has_stock,
      page: page - 1,
      size: size,
    };
    const queryString = getSearchFromQuery(location.search);

    if (queryString) {
      params.name = queryString.name;
    }

    if (categoriesSelected.length > 0) {
      params.categories = categoriesSelected.toString();
    }
    // console.log(params);
    return params;
  }, [categoriesSelected, filterObject, location.search, page, size]);

  // const [prevQueryFilter, setPrevQueryFilter] = useState(queryFilter);

  useEffect(() => {
    const queryFilter = buildQueryParamsCallback();
    fetchProducts({queryFilter});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildQueryParamsCallback]);

  useEffect(() => {
    fetchFeaturedProducts();
  }, [fetchFeaturedProducts]);

  useEffect(() => {
    if (featuredProducts) {
      setFeaturedProductsOrder(featuredProducts);
    }
  }, [featuredProducts]);

  useEffect(() => {
    return () => resetProductState();
  }, [resetProductState]);

  const handleCreateClick = () => {
    resetProductState();
    history.push(`/products/new`);
  };

  const handleProductEnable = useCallback(
    (id, state) => {
      const productRequest: ProductRequest = {
        id,
        enabled: !state,
        featured: state ? false : undefined,
      };
      enableProduct({productRequest});
    },
    [enableProduct]
  );

  const [tierRestrictionModal, setTierRestrictionModal] = useState(false);
  const [isFeaturedSectionCollapsed, setIsFeaturedSectionCollapsed] = useState(
    false
  );
  const [featuredProductsOrder, setFeaturedProductsOrder] = useState<Product[]>(
    []
  );

  const hasProductCatalogueFeature = useIsFeatureInTier('PRODUCT_CATALOGUE');
  const hasFeaturedProductsFeature = useIsFeatureInTier('FEATURED_PRODUCTS');
  const userHasInstagramProductUploadFeature = useIsFeatureInTier(
    'INSTAGRAM_PRODUCT_UPLOAD'
  );

  const handleProductFeature = useCallback(
    (id, state) => {
      if (!hasFeaturedProductsFeature) {
        setTierRestrictionModal(true);
      } else {
        const productRequest: ProductRequest = {
          id,
          featured: !state,
        };
        enableProduct({productRequest});
      }
    },
    [enableProduct, hasFeaturedProductsFeature]
  );

  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [editingStockProductId, setEditingStockProductId] = useState('');

  const hasStockByProductFeature = useIsFeatureInTier('STOCK_BY_PRODUCT');

  const handleStockModalOpen = useCallback(
    (id: string) => {
      if (!hasStockByProductFeature) {
        setTierRestrictionModal(true);
      } else {
        setStockModalOpen(true);
        setEditingStockProductId(id);
      }
    },
    [hasStockByProductFeature]
  );
  const handleStockModalClose = useCallback(() => {
    setStockModalOpen(false);
    setEditingStockProductId('');
  }, []);
  const handleChangeProductStock = useCallback(
    (stock: number) => {
      const productRequest: ProductRequest = {
        id: editingStockProductId,
        stockSetting: {stock: stock},
      };
      enableProduct({productRequest});
    },
    [editingStockProductId, enableProduct]
  );

  const handleEditClick = useCallback(
    (product?: Product) => {
      if (product) {
        resetProductState();
        history.push(`/products/${product.id}`);
      }
    },
    [history, resetProductState]
  );

  const handleSortFeaturedEnd = useCallback(
    (oldIndex: number, newIndex: number) => {
      const sortedProducts = arrayMove(
        featuredProductsOrder,
        oldIndex,
        newIndex
      );
      setFeaturedProductsOrder(sortedProducts);

      const movement = {
        init: {
          id: featuredProductsOrder[oldIndex]?.id,
          pos: oldIndex,
        },
        end: {
          id: featuredProductsOrder[newIndex]?.id,
          pos: newIndex,
        },
      };

      console.log('Featured products reordered:', movement);
    },
    [featuredProductsOrder]
  );

  const handleDuplicateProduct = useCallback(
    (product: Product) => {
      resetProductState();
      history.push(`/products/${product.id}/duplicate`);
    },
    [history, resetProductState]
  );

  const handleCategoryOption = useCallback(
    (id: string) => {
      // if (id !== categorySelected) {
      //   setCategorySelected(value);
      // }
      categoriesSelected.indexOf(id) === -1
        ? setCategoriesSelected([...categoriesSelected, id])
        : setCategoriesSelected(categoriesSelected.filter(e => e !== id));
    },
    [categoriesSelected]
  );

  const categoryErrorMessage = useMemo(() => {
    if (createCategoryError && createCategoryError.status === 403)
      return 'Se llegó al número máximo de categorías permitidas';
    if (typeof createCategoryError === 'string') {
      return createCategoryError;
    } else return createCategoryError && createCategoryError.error.message;
  }, [createCategoryError]);

  const handlePageChange = (event, value) => {
    setPage(value);
    scrollTop('smooth');
  };

  const handleSizeChange = event => {
    if (event) {
      setSize(event.target.value);
    }
  };

  const categoryEdit = queryString.parse(location.search).categoryEdit;
  const [editCategoryModalOpen, setEditCategoryModalOpen] = useState(
    !!categoryEdit
  );
  const handleEditCategoryModalClose = useCallback(() => {
    setEditCategoryModalOpen(false);
    const q = new URLSearchParams(location.search);
    if (q.has('categoryEdit')) q.delete('categoryEdit');
    history.replace({search: q.toString()});
  }, [history, location.search]);

  const handleGoToCategories = () => {
    history.push('/categories');
  };
  const editCategoriesButton = variant => (
    <CustomButton
      variant={variant === 'mobile' ? 'text' : 'contained'}
      className={
        variant === 'mobile'
          ? classes.mobileEditCategoriesButton
          : classes.editCategoriesButton
      }
      startIcon={
        categories && categories.length > 0 ? <Edit /> : <AddCircleOutline />
      }
      onClick={handleGoToCategories}
      id="create-edit-categories-button"
    >
      {categories && categories.length > 0
        ? 'Editar Categorías'
        : 'Crear Categoría'}
    </CustomButton>
  );

  const instagramUpload = queryString.parse(location.search).instagramUpload;
  const [instagramProductsModalOpen, setInstagramProductsModalOpen] = useState(
    !!instagramUpload
  );
  const handleInstagramProductsModalClose = () => {
    setInstagramProductsModalOpen(false);
    const q = new URLSearchParams(location.search);
    if (q.has('instagramUpload')) q.delete('instagramUpload');
    history.replace({search: q.toString()});
  };

  useEffect(() => {
    if (!isCreatingCatalogue && createCatalogueSuccess) {
      setIsFetchingCatalogue(true);
    }
  }, [createCatalogueSuccess, isCreatingCatalogue]);

  useEffect(() => {
    if (isFetchingCatalogue && hasProductCatalogueFeature) {
      setIsFetchingCatalogue(true);
      const id = setInterval(() => {
        fetchProductsCatalogue();
        setCount(v => v + 1);
      }, INTERVAL);

      if (Number(previousCount) > LIMIT_TRIES) {
        clearInterval(id);
        setIsFetchingCatalogue(false);
        setShowMessage(true);
      }

      if (catalogue && catalogue.state === 'FAILED') {
        clearInterval(id);
        setIsFetchingCatalogue(false);
        setShowMessage(true);
      }

      if (catalogue && (!!catalogue.fileId || catalogue.state === 'BUILT')) {
        clearInterval(id);
        setIsFetchingCatalogue(false);
        setCount(0);
        setShowMessage(false);
      }
      return () => {
        clearInterval(id);
        setIsFetchingCatalogue(false);
      };
    }
  }, [
    catalogue,
    fetchProductsCatalogue,
    hasProductCatalogueFeature,
    isFetchingCatalogue,
    previousCount,
  ]);

  useEffect(() => {
    if (
      typeof isFetchingCatalogue === 'boolean' &&
      !isFetchingCatalogue &&
      catalogue &&
      catalogue.fileId
    ) {
      if (window) {
        const url = getImageUrl(catalogue.fileId);
        window.open(url, '_blank');
      }
    }
  }, [catalogue, isFetchingCatalogue]);

  const searchProductEnabled = useSearchProductEnabled();

  const paginationArea = (
    <div className={classes.paginationArea}>
      <Pagination
        count={
          pagination &&
          (pagination.size >= pagination.total
            ? 1
            : Math.ceil(pagination.total / pagination.size))
        }
        page={page}
        className={classes.pagination}
        color="primary"
        onChange={handlePageChange}
      />
      <Hidden smUp>
        <CustomSelect
          native
          pagination
          disableUnderline
          className={classes.sizeSelect}
          value={size}
          onChange={handleSizeChange}
        >
          <option value={8}>8 por página</option>
          <option value={16}>16 por página</option>
          <option value={24}>24 por página</option>
          <option value={32}>32 por página</option>
        </CustomSelect>
      </Hidden>
      <Hidden xsDown>
        <CustomSelect
          pagination
          disableUnderline
          className={classes.sizeSelect}
          value={size}
          onChange={handleSizeChange}
        >
          <MenuItem value={8}>8 por página</MenuItem>
          <MenuItem value={16}>16 por página</MenuItem>
          <MenuItem value={24}>24 por página</MenuItem>
          <MenuItem value={32}>32 por página</MenuItem>
        </CustomSelect>
      </Hidden>
    </div>
  );

  const filterArea = (
    <div className={classes.filterArea}>
      <Typography color="textSecondary" className={classes.filterLabel}>
        Mostrar por:
      </Typography>
      <Hidden smUp>
        <CustomSelect
          native
          pagination
          disableUnderline
          // className={classes.sizeSelect}
          value={filter}
          onChange={handleSortChange}
        >
          <option value={''} disabled>
            Mostrar por:
          </option>
          <option aria-label="None" value={'--'}>
            Mostrar Todos
          </option>
          {/* <option value={'Productos con stock'}>Productos con stock</option> */}
          <option value={'Productos sin stock'}>Productos sin stock</option>
          <option value={'Productos habilitados'}>Productos habilitados</option>
          <option value={'Productos deshabilitados'}>
            Productos deshabilitados
          </option>
          <option value={'Nombre A-Z'}>Nombre A-Z</option>
          <option value={'Nombre Z-A'}>Nombre Z-A</option>
          <option value={'Precio Ascendente'}>Precio Ascendente</option>
          <option value={'Precio Descendente'}>Precio Descendente</option>
        </CustomSelect>
      </Hidden>
      <Hidden xsDown>
        <CustomSelect
          pagination
          disableUnderline
          // className={classes.sizeSelect}
          value={filter}
          onChange={handleSortChange}
        >
          <MenuItem aria-label="None" value={'--'}>
            Mostrar Todos
          </MenuItem>
          <MenuItem value={'Productos sin stock'}>Productos sin stock</MenuItem>
          <MenuItem value={'Productos habilitados'}>
            Productos habilitados
          </MenuItem>
          <MenuItem value={'Productos deshabilitados'}>
            Productos deshabilitados
          </MenuItem>
          <MenuItem value={'Nombre A-Z'}>Nombre A-Z</MenuItem>
          <MenuItem value={'Nombre Z-A'}>Nombre Z-A</MenuItem>
          <MenuItem value={'Precio Ascendente'}>Precio Ascendente</MenuItem>
          <MenuItem value={'Precio Descendente'}>Precio Descendente</MenuItem>
        </CustomSelect>
      </Hidden>
    </div>
  );

  return (
    <div className={classes.root}>
      <SnackBar
        mode="success"
        message={createProductSuccess ? 'Producto Creado' : ''}
      />
      <SnackBar
        mode="success"
        message={editProductSuccess ? 'Producto Guardado' : ''}
      />
      <SnackBar
        mode="success"
        message={deleteProductSuccess ? 'Producto Eliminado' : ''}
      />
      <SnackBar mode="error" message={categoryErrorMessage} />
      <SnackBar
        mode="success"
        message={enableProductSuccess ? 'Producto activado' : ''}
      />
      <SnackBar
        mode="warning"
        message={disableProductSuccess ? 'Producto desactivado' : ''}
      />
      <SnackBar
        mode="success"
        message={productFeaturedSuccess ? 'Producto agregado a destacados' : ''}
      />
      <SnackBar
        mode="success"
        message={
          productUnfeaturedSuccess ? 'Producto quitado de destacados' : ''
        }
      />
      <SnackBar
        mode="success"
        message={stockChangeSuccess ? 'Stock actualizado' : ''}
      />
      <SnackBar
        mode="error"
        message={enableProductError ? enableProductError : ''}
      />
      <SnackBar
        mode="success"
        message={createCategorySuccess ? 'Categoría creada' : ''}
      />
      <SnackBar
        mode="success"
        message={editCategorySuccess ? 'Categoría actualizada' : ''}
      />
      <SnackBar
        mode="error"
        message={editCategoryError ? 'Error al actualizar categoría' : ''}
      />
      <SnackBar
        mode="success"
        message={deleteCategorySuccess ? 'Categoría eliminada' : ''}
      />
      <SnackBar
        mode="success"
        message={sortCategoriesSuccess ? 'Categorías ordenadas' : ''}
      />
      <SnackBar
        mode="error"
        message={
          showMessage || createCatalogueError
            ? 'No se pudo obtener el catálogo'
            : ''
        }
      />

      {/* {appBar} */}
      <AlertDueDateTier />
      <Hidden mdUp>
        <div className={classes.subRouteNameWrap}>
          <Typography className={classes.title}>
            {businessModel === 'SERVICE' ? 'Mis Servicios' : 'Mis Productos'}
          </Typography>
        </div>
      </Hidden>
      <main className={classes.content}>
        <div className={classes.headerWrapper}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Hidden smDown>
                <Typography className={classes.title}>
                  {businessModel === 'SERVICE'
                    ? 'Mis Servicios'
                    : 'Mis Productos'}
                </Typography>
              </Hidden>
              <Hidden mdUp>
                <Typography className={classes.titleCategories}>
                  Categorías
                </Typography>
              </Hidden>
            </Grid>
            <Grid item>
              {userHasInstagramProductUploadFeature &&
                businessModel === 'PRODUCT' && (
                  <CustomButton
                    variant="contained"
                    className={classes.instagramProductUploadButton}
                    onClick={() => setInstagramProductsModalOpen(true)}
                    startIcon={
                      <img
                        style={{height: 20, width: 20}}
                        src={miniInstagramIcon}
                        alt=""
                      />
                    }
                  >
                    Subir productos desde Instagram
                  </CustomButton>
                )}
              {store && store.storeTier && store.storeTier.name < 'TIER_3' && (
                <CustomButton
                  className={classes.improveTierButton}
                  to="/tiers-list"
                >
                  Mejorar Plan
                </CustomButton>
              )}

              <CustomButton
                href={TIENDADA_LINKS.tutorials.PRODUCTS}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.tutorialsButton}
                startIcon={<PlayCircleFilledTwoTone />}
              >
                Tutoriales
              </CustomButton>
            </Grid>
          </Grid>
          <HorizontalFilter>
            <Hidden xsDown>{editCategoriesButton()}</Hidden>
            <HorizontalScroll>
              {categories &&
                categories.map((c: Category, index: number) => (
                  <TiendadaTooltip
                    key={c.id}
                    title={'Esta categoría está deshabilitada'}
                    disableHoverListener={c.enabled}
                    placement="top"
                  >
                    <div>
                      <FilterButton
                        key={index}
                        classes={{
                          root: c.enabled ? '' : classes.categoryDisabled,
                        }}
                        selected={categoriesSelected.indexOf(c.id) !== -1}
                        id={c.id}
                        handleOnClick={handleCategoryOption}
                        text={c.name}
                      />
                    </div>
                  </TiendadaTooltip>
                ))}
            </HorizontalScroll>
          </HorizontalFilter>
        </div>
        <div className={classes.productsContent}>
          <SearchBar
            placeholder="Buscar producto"
            disabled={!searchProductEnabled}
          />
          {categoriesSelected.length === 0 &&
            (filter === '' || filter === '--') &&
            store &&
            hasFeaturedProductsFeature && (
              <div className={classes.productosDestacados}>
                <div
                  className={classes.productosDestacadosTitle}
                  onClick={() =>
                    setIsFeaturedSectionCollapsed(!isFeaturedSectionCollapsed)
                  }
                  role="button"
                  tabIndex={0}
                  onKeyPress={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setIsFeaturedSectionCollapsed(
                        !isFeaturedSectionCollapsed
                      );
                    }
                  }}
                >
                  <span className={classes.productosDestacadosTitleText}>
                    Elementos destacados
                  </span>
                  <IconButton
                    className={classes.productosDestacadosCollapseButton}
                    size="small"
                    onClick={e => {
                      e.stopPropagation();
                      setIsFeaturedSectionCollapsed(
                        !isFeaturedSectionCollapsed
                      );
                    }}
                  >
                    {isFeaturedSectionCollapsed ? (
                      <ExpandMore />
                    ) : (
                      <ExpandLess />
                    )}
                  </IconButton>
                </div>
                <div
                  className={classNames(classes.productosDestacadosSlider, {
                    [classes.productosDestacadosSliderCollapsed]: isFeaturedSectionCollapsed,
                  })}
                >
                  <Grid
                    item
                    xs={12}
                    key="create-card"
                    style={{flexBasis: 'unset'}}
                  >
                    <FeaturedProductCard
                      type="CREATE"
                      text="Haz click en la estrella para que ese producto aparezca en destacados"
                    />
                  </Grid>
                  {isFetchingFeatured && !featuredProducts
                    ? Array.from({length: 6}, (x, i) => (
                        <Grid
                          item
                          xs={12}
                          key={`destacado-skeleton-${i}`}
                          style={{flexBasis: 'unset'}}
                        >
                          <Skeleton
                            variant="rect"
                            height={mobile ? 240 : 300}
                            width={mobile ? 240 : 300}
                            style={{marginRight: mobile ? 16 : 24}}
                          />
                        </Grid>
                      ))
                    : featuredProductsOrder &&
                      featuredProductsOrder.length > 0 && (
                        <SortableList
                          className={classes.sortableContainer}
                          draggedItemClassName={classes.draggedItem}
                          onSortEnd={handleSortFeaturedEnd}
                        >
                          {featuredProductsOrder.map((p: Product) => (
                            <Grid
                              item
                              xs={12}
                              key={p.id}
                              style={{flexBasis: 'unset'}}
                            >
                              <FeaturedProductCard
                                type="EDIT"
                                product={p}
                                currency={currency}
                                handleProductEnable={handleProductEnable}
                                handleProductFeature={handleProductFeature}
                                handleStockModalOpen={handleStockModalOpen}
                                handleClick={handleEditClick}
                              />
                            </Grid>
                          ))}
                        </SortableList>
                      )}
                </div>
              </div>
            )}
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            // spacing={3}
            className={classes.topBar}
          >
            <Grid item>
              <Typography
                variant="body1"
                color="textSecondary"
                className={classes.totalItems}
              >
                En esta página:{' '}
                {products && pagination
                  ? `${products.length}/${pagination.total}`
                  : '0/0'}
              </Typography>
            </Grid>
            {/* <Hidden xsDown> */}
            <Grid item>{filterArea}</Grid>
            {/* </Hidden> */}
          </Grid>
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="stretch"
            spacing={2}
          >
            <Grid item xs={6} sm={6} md={4} lg={4} xl={2} key="1">
              <ProductCard
                type="CREATE"
                handleClick={handleCreateClick}
                text={
                  businessModel === 'SERVICE'
                    ? 'Crear Servicio'
                    : 'Crear Producto'
                }
              />
            </Grid>
            {isFetching && !products
              ? Array.from({length: size - 1}, (x, i) => (
                  <Grid item xs={6} sm={6} md={4} lg={4} xl={2} key={i}>
                    <Skeleton variant="rect" height={320} />
                  </Grid>
                ))
              : products && products.length > 0
              ? products.map((p: Product) => (
                  <Grid item xs={6} sm={6} md={4} lg={4} xl={2} key={p.id}>
                    <ProductCard
                      type="EDIT"
                      product={p}
                      currency={currency}
                      handleProductEnable={handleProductEnable}
                      handleProductFeature={handleProductFeature}
                      handleStockModalOpen={handleStockModalOpen}
                      handleClick={handleEditClick}
                      onDuplicate={handleDuplicateProduct}
                    />
                  </Grid>
                ))
              : Array.from({length: size - 1}, (x, i) => (
                  <Grid item xs={6} sm={6} md={4} lg={4} xl={2} key={i}>
                    <ProductCard />
                  </Grid>
                ))}
          </Grid>
          {paginationArea}
        </div>
      </main>
      {editCategoryModalOpen && (
        <EditCategoryModal
          open={editCategoryModalOpen}
          handleClose={handleEditCategoryModalClose}
          categories={categories}
          isSaving={isSavingCategory}
          createCategory={createCategory}
          editCategory={editCategory}
          createCategorySuccess={createCategorySuccess}
          sortCategories={sortCategories}
          deleteCategory={deleteCategory}
          resetState={resetState}
        />
      )}
      {products && (
        <StockChangeModal
          open={stockModalOpen}
          handleClose={handleStockModalClose}
          handleChangeProductStock={handleChangeProductStock}
          editProductVariant={editProductVariant}
          product={products.find(p => p.id === editingStockProductId)}
          isSaving={isSaving}
        />
      )}
      <InstagramProductsModal
        open={instagramProductsModalOpen}
        handleClose={handleInstagramProductsModalClose}
      />
      <TierRestrictionModal
        open={tierRestrictionModal}
        handleClose={() => setTierRestrictionModal(false)}
      />
    </div>
  );
}

Products.propTypes = {
  fetchProducts: PropTypes.func.isRequired,
  fetchFeaturedProducts: PropTypes.func.isRequired,
  createProduct: PropTypes.func.isRequired,
  createProductSuccess: PropTypes.bool,
  editProduct: PropTypes.func.isRequired,
  editProductSuccess: PropTypes.bool,
  deleteProduct: PropTypes.func.isRequired,
  deleteProductSuccess: PropTypes.bool,
  products: PropTypes.array,
  featuredProducts: PropTypes.array,
  pagination: PropTypes.object,
  queryFilter: PropTypes.object,
  isFetchingFeatured: PropTypes.bool,
  enableProduct: PropTypes.func.isRequired,
  enableProductSuccess: PropTypes.bool,
  disableProductSuccess: PropTypes.bool,
  productFeaturedSuccess: PropTypes.bool,
  productUnfeaturedSuccess: PropTypes.bool,
  stockChangeSuccess: PropTypes.bool,
  enableProductError: PropTypes.object,
  editProductVariant: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  isSaving: PropTypes.bool,
  productOperationError: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]),
  user: PropTypes.object,
  store: PropTypes.object,
  isSavingCategory: PropTypes.bool,
  currency: PropTypes.object,
  information: PropTypes.object,
  resellerInformation: PropTypes.object,
  categories: PropTypes.array,
  createCategory: PropTypes.func.isRequired,
  editCategory: PropTypes.func.isRequired,
  deleteCategory: PropTypes.func.isRequired,
  deleteCategorySuccess: PropTypes.bool,
  createCategorySuccess: PropTypes.bool,
  editCategorySuccess: PropTypes.bool,
  editCategoryError: PropTypes.bool,
  sortCategories: PropTypes.func.isRequired,
  sortCategoriesSuccess: PropTypes.bool,
  createCategoryError: PropTypes.object,
  resetCategoryState: PropTypes.func,
  resetProductState: PropTypes.func,
  fetchProductsCatalogue: PropTypes.func.isRequired,
  createProductsCatalogue: PropTypes.func.isRequired,
  catalogue: PropTypes.object,
  isCreatingCatalogue: PropTypes.bool,
  createCatalogueSuccess: PropTypes.bool,
  createCatalogueError: PropTypes.string,
};
